import React, { useState } from 'react';
import { FaCheck, FaArrowRight, FaChartLine, FaUsers, FaBrain, FaCrosshairs, FaCogs, FaRocket } from 'react-icons/fa';
import { WAITLIST_ONLY } from '../config';
import LoginOverlay from './LoginOverlay';
import authService from '../services/authService';

function PricingPage({ onWaitlistClick }) {
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [currentStep, setCurrentStep] = useState('assessment'); // assessment, plans, stepper
  // eslint-disable-next-line no-unused-vars
  const [recommendedPlan, setRecommendedPlan] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState(null);

  const businessGoals = [
    {
      id: 'ai-insights',
      title: 'AI-Powered Business Intelligence',
      description: 'Get smart recommendations for growth',
      icon: <FaBrain className="goal-icon" />
    },
    {
      id: 'strategic-planning',
      title: 'Strategic Growth Planning',
      description: 'Create data-driven business strategies',
      icon: <FaCrosshairs className="goal-icon" />
    },
    {
      id: 'market-analysis',
      title: 'Market & Competitor Analysis',
      description: 'Understand your competitive landscape',
      icon: <FaChartLine className="goal-icon" />
    },
    {
      id: 'automation',
      title: 'Business Process Automation',
      description: 'Automate repetitive tasks with AI',
      icon: <FaCogs className="goal-icon" />
    },
    {
      id: 'customer-insights',
      title: 'Customer Behavior Analytics',
      description: 'Understand and predict customer needs',
      icon: <FaUsers className="goal-icon" />
    },
    {
      id: 'growth-optimization',
      title: 'Revenue Optimization Tools',
      description: 'Maximize profitability with AI insights',
      icon: <FaRocket className="goal-icon" />
    }
  ];

  // Dynamic pricing based on selected goals
  const getPersonalizedPricing = () => {
    const goalCount = selectedGoals.length;
    const hasAutomation = selectedGoals.includes('automation');
    const hasGrowthOptimization = selectedGoals.includes('growth-optimization');
    const hasStrategicPlanning = selectedGoals.includes('strategic-planning');

    // Enterprise tier pricing varies based on complexity of selected goals
    let enterprisePrice = 149;
    let enterpriseOriginal = 199;
    if (hasAutomation && hasGrowthOptimization && hasStrategicPlanning) {
      enterprisePrice = 199;
      enterpriseOriginal = 279;
    } else if (hasGrowthOptimization && goalCount >= 4) {
      enterprisePrice = 179;
      enterpriseOriginal = 249;
    } else if (hasAutomation && goalCount >= 3) {
      enterprisePrice = 169;
      enterpriseOriginal = 229;
    }

    return {
      starter: { price: 29, originalPrice: 39, discount: '25% off' },
      growth: { price: 79, originalPrice: 99, discount: '20% off' },
      professional: { price: enterprisePrice, originalPrice: enterpriseOriginal, discount: `${Math.round((1 - enterprisePrice/enterpriseOriginal) * 100)}% off` }
    };
  };

  const personalizedPricing = getPersonalizedPricing();

  const pricingPlans = [
    {
      id: 'starter',
      name: 'AI Essentials',
      price: personalizedPricing.starter.price,
      originalPrice: personalizedPricing.starter.originalPrice,
      discount: personalizedPricing.starter.discount,
      recommended: false,
      description: 'Essential AI tools for small business owners',
      features: [
        'Basic AI business insights',
        'Monthly growth recommendations',
        'Customer behavior tracking',
        'Simple goal setting & tracking',
        'Email support',
        'Basic market analysis',
        '5 AI-generated reports/month'
      ],
      idealFor: ['ai-insights', 'customer-insights']
    },
    {
      id: 'growth',
      name: 'Intelligence Pro',
      price: personalizedPricing.growth.price,
      originalPrice: personalizedPricing.growth.originalPrice,
      discount: personalizedPricing.growth.discount,
      recommended: true,
      description: 'Advanced AI intelligence for scaling businesses',
      features: [
        'Everything in AI Essentials',
        'Advanced AI business intelligence',
        'Real-time competitor monitoring',
        'Automated workflow optimization',
        'Predictive analytics & forecasting',
        'Weekly AI strategy reports',
        'Priority support',
        'Custom KPI dashboards',
        'Unlimited AI reports',
        'Team collaboration tools'
      ],
      idealFor: ['ai-insights', 'strategic-planning', 'market-analysis', 'automation', 'customer-insights']
    },
    {
      id: 'professional',
      name: 'Enterprise AI',
      price: personalizedPricing.professional.price,
      originalPrice: personalizedPricing.professional.originalPrice,
      discount: personalizedPricing.professional.discount,
      description: 'Complete AI-powered business command center',
      features: [
        'Everything in Intelligence Pro',
        'Custom AI model training',
        'Multi-location intelligence',
        'Advanced revenue optimization',
        'AI-powered decision support',
        'Dedicated AI strategist',
        'API access & integrations',
        'White-label AI reports',
        'Custom automation workflows',
        '24/7 priority support'
      ],
      idealFor: ['ai-insights', 'strategic-planning', 'market-analysis', 'automation', 'customer-insights', 'growth-optimization']
    }
  ];

  const handleGoalToggle = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };

  const getRecommendedPlan = () => {
    if (selectedGoals.length <= 2) return pricingPlans[0];
    if (selectedGoals.length <= 4) return pricingPlans[1];
    return pricingPlans[2];
  };

  const handleContinue = () => {
    const recommended = getRecommendedPlan();
    setRecommendedPlan(recommended);
    setCurrentStep('plans');
  };

  const handlePlanSelect = (plan) => {
    setRecommendedPlan(plan);
    // Waitlist-only: never open payment / account setup / dashboard path
    if (WAITLIST_ONLY) {
      if (typeof onWaitlistClick === 'function') {
        onWaitlistClick();
      } else {
        setShowWaitlist(true);
      }
      return;
    }
    setCurrentStep('stepper');
  };

  const handleJoinWaitlist = async (email) => {
    setWaitlistLoading(true);
    setWaitlistError(null);
    try {
      await authService.joinWaitlist(email);
    } catch (err) {
      setWaitlistError(err?.message || 'Could not join waitlist');
      throw err;
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Payment stepper disabled while waitlist-only
  if (currentStep === 'stepper' && !WAITLIST_ONLY) {
    return null;
  }

  return (
    <div className="page-content">
      <div className="pricing-container">
        {currentStep === 'assessment' && (
          <div className="assessment-section">
            <div className="assessment-header">
              <h1>I want to</h1>
              <p>Select your business intelligence needs to get a personalized AI solution</p>
            </div>
            
            <div className="goals-grid">
              {businessGoals.map(goal => (
                <div 
                  key={goal.id}
                  className={`goal-card ${selectedGoals.includes(goal.id) ? 'selected' : ''}`}
                  onClick={() => handleGoalToggle(goal.id)}
                >
                  {goal.icon}
                  <h3>{goal.title}</h3>
                  <p>{goal.description}</p>
                  {selectedGoals.includes(goal.id) && (
                    <div className="selected-indicator">
                      <FaCheck />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="assessment-footer">
              <button 
                className="continue-button"
                onClick={handleContinue}
                disabled={selectedGoals.length === 0}
              >
                Continue
                <FaArrowRight />
              </button>
            </div>
          </div>
        )}

        {currentStep === 'plans' && (
          <div className="plans-section">
            <div className="plans-header">
              <h1>Choose Your AI Intelligence Plan</h1>
              <p>Based on your business needs, we recommend the {getRecommendedPlan().name} plan</p>
            </div>

            <div className="pricing-grid">
              {pricingPlans.map(plan => (
                <div 
                  key={plan.id}
                  className={`pricing-card ${plan.id === getRecommendedPlan().id ? 'recommended' : ''}`}
                >
                  {plan.id === getRecommendedPlan().id && (
                    <div className="recommended-badge">
                      Best for Your Goals
                    </div>
                  )}
                  
                  <div className="plan-header">
                    <h3>{plan.name}</h3>
                    <p className="plan-description">{plan.description}</p>
                    {plan.id === 'professional' ? (
                      <>
                        <div className="price-container">
                          <span className="price custom-price">Custom</span>
                        </div>
                        <p className="billing-note">Tailored to your business needs</p>
                      </>
                    ) : (
                      <>
                        <div className="price-container">
                          <span className="currency">$</span>
                          <span className="price">{plan.price}</span>
                          <span className="period">/month</span>
                        </div>
                        <div className="original-price">
                          <span className="original">${plan.originalPrice}</span>
                          <span className="discount">{plan.discount}</span>
                        </div>
                        <p className="billing-note">Billed annually</p>
                      </>
                    )}
                  </div>

                  <div className="features-list">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <FaCheck className="feature-check" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className={`plan-button ${plan.id === getRecommendedPlan().id ? 'recommended' : ''} ${plan.id === 'professional' ? 'contact-sales' : ''}`}
                    onClick={() => plan.id === 'professional' ? window.location.href = 'mailto:sales@broaderbusiness.com' : handlePlanSelect(plan)}
                  >
                    {plan.id === 'professional'
                      ? 'Contact Sales'
                      : WAITLIST_ONLY
                        ? 'Join Waitlist'
                        : (plan.id === getRecommendedPlan().id ? 'Get Started' : 'Choose Plan')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {WAITLIST_ONLY && (
        <LoginOverlay
          isAuthenticated={false}
          error={waitlistError}
          loading={waitlistLoading}
          joinWaitlist={handleJoinWaitlist}
          onClose={() => setShowWaitlist(false)}
          showOverlay={showWaitlist}
          defaultMode="signup"
        />
      )}
    </div>
  );
}

export default PricingPage;